/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  AlertCircle,
  FileText,
  ListChecks,
  MessageCircle,
  Mail,
  Shield,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import {
  trackDiagnosticCtaClicked,
  trackDiagnosticStarted,
  trackDiagnosticStepCompleted,
  trackDiagnosticCompleted,
  trackDiagnosticEmailSubmitted,
  trackDiagnosticToUploadClicked,
} from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { idx: 1, key: 'goal',     label: 'Votre besoin' },
  { idx: 2, key: 'family',   label: 'Situation' },
  { idx: 3, key: 'assets',   label: 'Patrimoine' },
  { idx: 4, key: 'docs',     label: 'Documents' },
];

const GOALS = [
  { value: 'succession_en_cours',   label: 'Une succession en cours' },
  { value: 'succession_a_anticiper', label: 'Une succession à anticiper' },
  { value: 'donation_a_preparer',   label: 'Une donation à préparer' },
  { value: 'droits_de_succession',  label: 'Les droits de succession possibles' },
  { value: 'protection_conjoint',   label: 'La protection du conjoint' },
  { value: 'repartition_heritiers', label: 'La répartition entre enfants ou héritiers' },
];

const FAMILY_STATUS = [
  { value: 'marié',       label: 'Marié(e)' },
  { value: 'pacsé',       label: 'Pacsé(e)' },
  { value: 'célibataire', label: 'Célibataire' },
  { value: 'divorcé',     label: 'Divorcé(e)' },
  { value: 'veuf',        label: 'Veuf / Veuve' },
];

const RESIDENCE_RANGES = [
  '< 150 000 €',
  '150 000 – 300 000 €',
  '300 000 – 600 000 €',
  '600 000 – 1 000 000 €',
  '> 1 000 000 €',
];
const SAVINGS_RANGES = [
  '< 10 000 €',
  '10 000 – 50 000 €',
  '50 000 – 150 000 €',
  '150 000 – 500 000 €',
  '> 500 000 €',
];

const TRIBOOL = [
  { value: 'oui', label: 'Oui' },
  { value: 'non', label: 'Non' },
  { value: 'je ne sais pas', label: 'Je ne sais pas' },
];
const BOOL = [
  { value: true,  label: 'Oui' },
  { value: false, label: 'Non' },
];

/* ----------------------- shared UI primitives ------------------------- */

const Eyebrow = ({ children }) => (
  <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] opacity-80 mb-3">
    {children}
  </p>
);

const SectionTitle = ({ children }) => (
  <h2 className="font-['Cormorant_Garamond'] font-medium text-[26px] sm:text-[34px] text-[#1A2530] leading-tight mb-2">
    {children}
  </h2>
);

const Pill = ({ active, children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    className={[
      "text-left font-['Work_Sans'] text-[14px] sm:text-[15px] rounded-2xl border px-5 py-4 leading-snug transition-all w-full",
      active
        ? 'bg-[#1A2530] text-white border-[#1A2530] shadow-md'
        : 'bg-white text-[#1A2530] border-[#E2DBCF] hover:border-[#1A2530] hover:bg-[#FDFBF7]',
    ].join(' ')}
  >
    {children}
  </button>
);

const NumberInput = ({ value, onChange, testId, min = 0, max = 12 }) => (
  <div className="inline-flex items-center gap-2">
    <button
      type="button"
      onClick={() => onChange(Math.max(min, (value ?? 0) - 1))}
      className="w-9 h-9 rounded-full border border-[#E2DBCF] bg-white text-[#1A2530] hover:bg-[#FDFBF7]"
      aria-label="Diminuer"
      data-testid={`${testId}-minus`}
    >
      −
    </button>
    <span
      data-testid={testId}
      className="font-['Cormorant_Garamond'] text-[28px] text-[#1A2530] w-10 text-center"
    >
      {value ?? 0}
    </span>
    <button
      type="button"
      onClick={() => onChange(Math.min(max, (value ?? 0) + 1))}
      className="w-9 h-9 rounded-full border border-[#E2DBCF] bg-white text-[#1A2530] hover:bg-[#FDFBF7]"
      aria-label="Augmenter"
      data-testid={`${testId}-plus`}
    >
      +
    </button>
  </div>
);

const StepHeader = ({ stepIdx }) => (
  <div className="flex items-center justify-between mb-7">
    <Link
      to="/"
      className="inline-flex items-center font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] hover:text-[#1A2530] transition-colors"
      data-testid="diagnostic-back-home"
    >
      <ArrowLeft className="w-3.5 h-3.5 mr-2" />
      Retour
    </Link>
    <div className="flex items-center gap-1.5" data-testid="diagnostic-progress">
      {STEPS.map((s) => (
        <span
          key={s.idx}
          className={`h-1.5 rounded-full transition-all ${
            s.idx <= stepIdx ? 'bg-[#1A2530] w-8' : 'bg-[#E2DBCF] w-4'
          }`}
        />
      ))}
    </div>
  </div>
);

/* --------------------------- main component --------------------------- */

const DiagnosticPage = () => {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(1);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  // Email is the only required field to receive the diagnostic recap;
  // the very act of submitting IS the transactional consent. The
  // marketing opt-in below is a SEPARATE, optional, NOT pre-checked
  // checkbox for future Hérita updates.
  const [emailState, setEmailState] = useState({
    email: '',
    marketingOptIn: false,
    submitting: false,
    sent: false,
    emailWillBeSent: true, // populated by the backend response
    sentMarketingOptIn: false, // snapshot at submit time for the success message
  });

  useEffect(() => {
    trackDiagnosticStarted();
  }, []);

  const set = (patch) => setAnswers((a) => ({ ...a, ...patch }));

  const next = () => {
    const stepKey = STEPS.find((s) => s.idx === stepIdx)?.key;
    trackDiagnosticStepCompleted(stepIdx, stepKey);
    if (stepIdx < STEPS.length) {
      setStepIdx(stepIdx + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      void compute();
    }
  };

  const prev = () => {
    if (stepIdx > 1) setStepIdx(stepIdx - 1);
  };

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/diagnostic/submit`, answers);
      setResult(data);
      trackDiagnosticCompleted({
        mainGoal: answers.main_goal,
        hasChildren: (answers.children_count || 0) > 0,
        familyStatus: answers.family_status,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error('Diagnostic indisponible', {
        description: e?.response?.data?.detail || 'Réessayez dans un instant.',
      });
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async () => {
    const email = emailState.email.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error('Email invalide');
      return;
    }
    setEmailState((s) => ({ ...s, submitting: true }));
    try {
      const { data } = await axios.post(`${API}/diagnostic/email`, {
        diagnostic_id: result?.diagnostic_id,
        email,
        marketing_opt_in: emailState.marketingOptIn,
        answers,
      });
      trackDiagnosticEmailSubmitted();
      setEmailState((s) => ({
        ...s,
        submitting: false,
        sent: true,
        // Backend tells us whether Resend was actually triggered. If
        // not, the success message must NOT promise an inbox delivery.
        emailWillBeSent: data?.email_will_be_sent !== false,
        sentMarketingOptIn: Boolean(data?.marketing_opt_in ?? s.marketingOptIn),
      }));
    } catch (e) {
      setEmailState((s) => ({ ...s, submitting: false }));
      toast.error('Envoi impossible', {
        description: e?.response?.data?.detail || 'Réessayez dans un instant.',
      });
    }
  };

  const canAdvance = useMemo(() => {
    if (stepIdx === 1) return Boolean(answers.main_goal);
    if (stepIdx === 2) return Boolean(answers.family_status);
    return true; // steps 3 & 4 are entirely optional
  }, [stepIdx, answers]);

  return (
    <div
      className="min-h-screen bg-[#F9F7F3] text-[#2C363F] flex flex-col"
      data-testid="page-diagnostic"
    >
      <SEO
        title="Diagnostic succession gratuit | HÉRITA"
        description="Répondez à quelques questions et obtenez un premier diagnostic gratuit sur votre succession, donation ou transmission patrimoniale avec HÉRITA."
        path="/diagnostic"
      />

      <header className="border-b border-[#E2DBCF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="font-['IBM_Plex_Mono'] text-[11px] sm:text-[12px] uppercase tracking-[0.28em] text-[#1A2530] hover:opacity-80 transition-opacity"
            data-testid="diagnostic-brand-link"
          >
            HÉRITA
          </Link>
          <span className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-70">
            Diagnostic gratuit
          </span>
        </div>
      </header>

      <main className="flex-1 px-5 sm:px-8 lg:px-12 py-10 sm:py-16">
        <div className="max-w-2xl mx-auto">
          {!result && (
            <>
              <StepHeader stepIdx={stepIdx} />

              {/* Step intro */}
              <div className="mb-9">
                <Eyebrow>Étape {stepIdx} sur {STEPS.length}</Eyebrow>
                <SectionTitle>
                  {stepIdx === 1 && 'Que souhaitez-vous comprendre ?'}
                  {stepIdx === 2 && 'Votre situation familiale'}
                  {stepIdx === 3 && 'Votre patrimoine'}
                  {stepIdx === 4 && 'Documents et décisions existantes'}
                </SectionTitle>
                <p className="font-['Work_Sans'] text-[14.5px] sm:text-[15.5px] text-[#2C363F] opacity-90 leading-relaxed">
                  {stepIdx === 1 && 'Une seule réponse — la plus proche de votre situation. Vous pourrez la préciser ensuite.'}
                  {stepIdx === 2 && 'Quelques informations pour comprendre votre cadre familial.'}
                  {stepIdx === 3 && 'Réponses approximatives — aucun document n\'est nécessaire à ce stade.'}
                  {stepIdx === 4 && 'Dernière étape avant votre diagnostic.'}
                </p>
              </div>

              {/* ---------------- Step 1 — goal ---------------- */}
              {stepIdx === 1 && (
                <div className="space-y-2.5" data-testid="diagnostic-step-1">
                  {GOALS.map((g) => (
                    <Pill
                      key={g.value}
                      active={answers.main_goal === g.value}
                      onClick={() => set({ main_goal: g.value })}
                      testId={`goal-${g.value}`}
                    >
                      {g.label}
                    </Pill>
                  ))}
                </div>
              )}

              {/* ---------------- Step 2 — family ---------------- */}
              {stepIdx === 2 && (
                <div className="space-y-7" data-testid="diagnostic-step-2">
                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Situation familiale</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {FAMILY_STATUS.map((f) => (
                        <Pill
                          key={f.value}
                          active={answers.family_status === f.value}
                          onClick={() => set({ family_status: f.value })}
                          testId={`family-${f.value}`}
                        >
                          {f.label}
                        </Pill>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Nombre d'enfants</p>
                    <NumberInput
                      value={answers.children_count}
                      onChange={(v) => set({ children_count: v })}
                      testId="children-count"
                    />
                  </div>

                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Un conjoint survivant est-il concerné ?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {BOOL.map((b) => (
                        <Pill
                          key={String(b.value)}
                          active={answers.has_surviving_spouse === b.value}
                          onClick={() => set({ has_surviving_spouse: b.value })}
                          testId={`spouse-${b.value}`}
                        >
                          {b.label}
                        </Pill>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Famille recomposée ?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {BOOL.map((b) => (
                        <Pill
                          key={String(b.value)}
                          active={answers.is_recomposed_family === b.value}
                          onClick={() => set({ is_recomposed_family: b.value })}
                          testId={`recomposed-${b.value}`}
                        >
                          {b.label}
                        </Pill>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ---------------- Step 3 — assets ---------------- */}
              {stepIdx === 3 && (
                <div className="space-y-7" data-testid="diagnostic-step-3">
                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Résidence principale</p>
                    <div className="grid grid-cols-2 gap-2">
                      {BOOL.map((b) => (
                        <Pill
                          key={String(b.value)}
                          active={answers.has_main_residence === b.value}
                          onClick={() => set({ has_main_residence: b.value })}
                          testId={`residence-${b.value}`}
                        >
                          {b.label}
                        </Pill>
                      ))}
                    </div>
                    {answers.has_main_residence && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {RESIDENCE_RANGES.map((r) => (
                          <Pill
                            key={r}
                            active={answers.estimated_residence_value === r}
                            onClick={() => set({ estimated_residence_value: r })}
                          >
                            {r}
                          </Pill>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Autres biens immobiliers ?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {BOOL.map((b) => (
                        <Pill
                          key={String(b.value)}
                          active={answers.has_other_real_estate === b.value}
                          onClick={() => set({ has_other_real_estate: b.value })}
                          testId={`other-re-${b.value}`}
                        >
                          {b.label}
                        </Pill>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Épargne / comptes bancaires</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {SAVINGS_RANGES.map((r) => (
                        <Pill
                          key={r}
                          active={answers.estimated_savings_range === r}
                          onClick={() => set({ estimated_savings_range: r })}
                        >
                          {r}
                        </Pill>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Assurance-vie ?</p>
                    <div className="grid grid-cols-3 gap-2">
                      {TRIBOOL.map((t) => (
                        <Pill
                          key={t.value}
                          active={answers.has_life_insurance === t.value}
                          onClick={() => set({ has_life_insurance: t.value })}
                          testId={`life-${t.value}`}
                        >
                          {t.label}
                        </Pill>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Dettes ou crédit immobilier ?</p>
                    <div className="grid grid-cols-3 gap-2">
                      {TRIBOOL.map((t) => (
                        <Pill
                          key={t.value}
                          active={answers.has_debt === t.value}
                          onClick={() => set({ has_debt: t.value })}
                          testId={`debt-${t.value}`}
                        >
                          {t.label}
                        </Pill>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ---------------- Step 4 — docs ---------------- */}
              {stepIdx === 4 && (
                <div className="space-y-7" data-testid="diagnostic-step-4">
                  {[
                    { key: 'has_testament',         label: 'Testament existant ?' },
                    { key: 'has_donation',          label: 'Donation déjà réalisée ?' },
                    { key: 'has_donation_partage',  label: 'Donation-partage déjà réalisée ?' },
                  ].map((q) => (
                    <div key={q.key}>
                      <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">{q.label}</p>
                      <div className="grid grid-cols-3 gap-2">
                        {TRIBOOL.map((t) => (
                          <Pill
                            key={t.value}
                            active={answers[q.key] === t.value}
                            onClick={() => set({ [q.key]: t.value })}
                            testId={`${q.key}-${t.value}`}
                          >
                            {t.label}
                          </Pill>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div>
                    <p className="font-['Work_Sans'] text-[13.5px] text-[#1A2530] mb-2.5 font-medium">Acte notarié déjà reçu ?</p>
                    <div className="grid grid-cols-2 gap-2">
                      {BOOL.map((b) => (
                        <Pill
                          key={String(b.value)}
                          active={answers.has_notarial_act === b.value}
                          onClick={() => set({ has_notarial_act: b.value })}
                          testId={`act-${b.value}`}
                        >
                          {b.label}
                        </Pill>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-9 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={prev}
                  disabled={stepIdx === 1}
                  className="font-['Work_Sans'] text-[13.5px] text-[#2C363F] hover:text-[#1A2530] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center"
                  data-testid="diagnostic-prev"
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                  Précédent
                </button>
                <Button
                  type="button"
                  onClick={next}
                  disabled={!canAdvance || loading}
                  className="bg-[#1A2530] hover:bg-[#2C363F] text-white px-7 py-5 rounded-full font-['Work_Sans'] font-medium text-[14.5px] sm:text-[15px] transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="diagnostic-next"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : stepIdx === STEPS.length ? (
                    <>Voir mon diagnostic<ChevronRight className="ml-2 h-4 w-4" /></>
                  ) : (
                    <>Continuer<ChevronRight className="ml-2 h-4 w-4" /></>
                  )}
                </Button>
              </div>
            </>
          )}

          {/* ------------------------ RESULT ----------------------- */}
          {result && (
            <div data-testid="diagnostic-result">
              <Eyebrow>Diagnostic gratuit</Eyebrow>
              <h1 className="font-['Cormorant_Garamond'] font-medium text-[32px] sm:text-[44px] text-[#1A2530] leading-[1.1] mb-6">
                Votre situation en résumé
              </h1>
              <p
                className="font-['Work_Sans'] text-[15.5px] sm:text-[16px] text-[#2C363F] leading-relaxed mb-9 bg-white border border-[#E2DBCF] rounded-2xl p-6 sm:p-8"
                data-testid="result-summary"
              >
                {result.situation_summary}
              </p>

              <ResultBlock
                icon={AlertCircle}
                title="Les points à surveiller"
                items={result.watch_points}
                testId="result-watch-points"
              />

              <ResultBlock
                icon={MessageCircle}
                title="Les questions à poser au notaire"
                items={result.notary_questions}
                testId="result-notary-questions"
              />

              <ResultBlock
                icon={ListChecks}
                title="Vos prochaines étapes"
                items={result.next_steps}
                testId="result-next-steps"
              />

              {/* Email capture — TWO clearly separated commitments:
                  (1) submitting the form sends the diagnostic recap to
                      the address provided (transactional, implicit
                      consent — no checkbox required);
                  (2) an optional, NOT pre-checked checkbox below for
                      visitors who additionally want to hear about
                      future Hérita features. */}
              {!emailState.sent ? (
                <div
                  className="mt-10 bg-white border-2 border-[#1A2530] rounded-2xl p-6 sm:p-9 shadow-md"
                  data-testid="result-email-capture"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <Mail className="h-5 w-5 text-[#1A2530] mt-0.5" />
                    <div>
                      <p className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] leading-tight">
                        Recevoir mon diagnostic complet par email
                      </p>
                      <p className="font-['Work_Sans'] text-[13.5px] text-[#2C363F] opacity-90 leading-relaxed mt-2">
                        Pour conserver une trace écrite de cette première
                        lecture et la partager facilement avec votre notaire.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 mt-5">
                    <input
                      type="email"
                      placeholder="votre@email.fr"
                      value={emailState.email}
                      onChange={(e) => setEmailState((s) => ({ ...s, email: e.target.value }))}
                      className="flex-1 px-4 py-3 rounded-full border border-[#E2DBCF] bg-[#FDFBF7] font-['Work_Sans'] text-[14px] text-[#1A2530] focus:outline-none focus:border-[#1A2530]"
                      data-testid="email-input"
                      aria-label="Adresse email pour recevoir le diagnostic"
                    />
                    <Button
                      type="button"
                      onClick={submitEmail}
                      disabled={emailState.submitting}
                      className="bg-[#1A2530] hover:bg-[#2C363F] text-white px-6 py-3 rounded-full font-['Work_Sans'] font-medium text-[14px] transition-colors"
                      data-testid="email-submit"
                    >
                      {emailState.submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Recevoir mon diagnostic'}
                    </Button>
                  </div>
                  <p className="mt-3 font-['Work_Sans'] text-[11.5px] text-[#2C363F] opacity-70 leading-relaxed">
                    En envoyant cette demande, vous acceptez que votre
                    diagnostic vous soit transmis par email à cette adresse.
                    Cette adresse ne sera utilisée pour rien d'autre sans
                    votre accord explicite ci-dessous.
                  </p>

                  <div className="mt-5 pt-5 border-t border-[#E2DBCF]">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailState.marketingOptIn}
                        onChange={(e) => setEmailState((s) => ({ ...s, marketingOptIn: e.target.checked }))}
                        className="mt-1"
                        data-testid="email-marketing-optin"
                      />
                      <span className="font-['Work_Sans'] text-[12.5px] text-[#2C363F] opacity-90 leading-relaxed">
                        <span className="font-medium text-[#1A2530]">Optionnel —</span>{' '}
                        J'accepte de recevoir, par email, les futures
                        évolutions et nouveautés d'HÉRITA. Désabonnement
                        possible à tout moment.
                      </span>
                    </label>
                  </div>
                </div>
              ) : (
                <div
                  className="mt-10 bg-[#F0F4EE] border border-[#2D6A4F]/30 rounded-2xl p-6 sm:p-8 flex items-start gap-3"
                  data-testid="result-email-sent"
                >
                  <CheckCircle2 className="h-6 w-6 text-[#2D6A4F] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-['Cormorant_Garamond'] font-medium text-[20px] sm:text-[22px] text-[#1A2530] leading-tight">
                      C'est bien reçu.
                    </p>
                    <p
                      className="font-['Work_Sans'] text-[13.5px] text-[#2C363F] leading-relaxed mt-2"
                      data-testid="email-sent-message"
                    >
                      {emailState.emailWillBeSent ? (
                        <>
                          Votre diagnostic arrive par email dans quelques
                          instants.
                          {emailState.sentMarketingOptIn && (
                            <>
                              {' '}
                              Nous vous tiendrons aussi informé des
                              prochaines évolutions d'Hérita.
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          Votre demande a bien été enregistrée. Nous
                          revenons vers vous très vite.
                          {emailState.sentMarketingOptIn && (
                            <>
                              {' '}
                              Nous vous tiendrons aussi informé des
                              prochaines évolutions d'Hérita.
                            </>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* CTA to existing upload tunnel */}
              <div
                className="mt-10 bg-[#FDFBF7] border border-[#E2DBCF] rounded-2xl p-6 sm:p-9"
                data-testid="result-upload-cta"
              >
                <div className="flex items-start gap-3 mb-3">
                  <FileText className="h-5 w-5 text-[#1A2530] mt-0.5" />
                  <p className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] leading-tight">
                    Vous avez déjà reçu un acte, une déclaration de succession
                    ou une donation-partage ?
                  </p>
                </div>
                <p className="font-['Work_Sans'] text-[13.5px] sm:text-[14px] text-[#2C363F] opacity-90 leading-relaxed">
                  Le diagnostic donne une première orientation. Pour une lecture
                  précise de votre document, importez votre acte notarié dans
                  HÉRITA.
                </p>
                <Button
                  type="button"
                  onClick={() => {
                    trackDiagnosticToUploadClicked();
                    navigate('/upload');
                  }}
                  className="mt-5 bg-[#1A2530] hover:bg-[#2C363F] text-white px-7 py-5 rounded-full font-['Work_Sans'] font-medium text-[14.5px] sm:text-[15px] transition-all duration-300 shadow-md hover:shadow-lg"
                  data-testid="result-upload-btn"
                >
                  Analyser mon document
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              {/* Disclaimer — strengthened: explicit on numeric figures
                  being estimates, on the diagnostic being non-binding,
                  and on Hérita NOT replacing a notary's advice. */}
              <div
                className="mt-10 bg-[#FDFBF7] border border-[#E2DBCF] rounded-2xl p-5 sm:p-7"
                data-testid="result-disclaimer"
              >
                <div className="flex items-start gap-3">
                  <Shield className="h-4.5 w-4.5 text-[#1A2530] flex-shrink-0 mt-0.5" />
                  <div className="font-['Work_Sans'] text-[12.5px] sm:text-[13px] text-[#2C363F] opacity-90 leading-relaxed">
                    <p className="font-medium text-[#1A2530] mb-1.5">
                      Important — portée de ce diagnostic
                    </p>
                    <p>
                      Ce diagnostic est une <strong>estimation indicative et
                      pédagogique</strong>, basée uniquement sur les réponses
                      que vous nous avez données. Tout chiffre, ordre de
                      grandeur ou orientation mentionnés ci-dessus restent
                      des estimations et ne constituent pas une analyse
                      juridique ou fiscale précise.
                    </p>
                    <p className="mt-2">
                      HÉRITA <strong>ne remplace pas le conseil d'un
                      notaire, d'un avocat ou d'un conseiller patrimonial</strong>.
                      Seul un professionnel pourra confirmer la stratégie
                      adaptée à votre situation, calculer précisément les
                      droits applicables et rédiger les actes nécessaires.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-[#E2DBCF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-['Work_Sans'] text-sm text-[#2C363F]">© 2026 Hérita. Tous droits réservés.</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center">
            <Link to="/mentions-legales" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530]">Mentions légales</Link>
            <Link to="/confidentialite" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530]">Confidentialité</Link>
            <Link to="/cgu" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530]">CGU</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const ResultBlock = ({ icon: Icon, title, items, testId }) => {
  if (!items || items.length === 0) return null;
  return (
    <section className="mt-8" data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-4">
        <Icon className="h-4.5 w-4.5 text-[#1A2530]" />
        <h2 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] leading-tight">
          {title}
        </h2>
      </div>
      <ul className="space-y-2.5 list-none p-0">
        {items.map((it, i) => (
          <li
            key={i}
            className="bg-white border border-[#E2DBCF] rounded-xl p-4 sm:p-5 flex items-start gap-3"
          >
            <Check className="h-3.5 w-3.5 text-[#1A2530] mt-1 flex-shrink-0" strokeWidth={3} />
            <span className="font-['Work_Sans'] text-[14px] sm:text-[14.5px] text-[#1A2530] leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default DiagnosticPage;
export { trackDiagnosticCtaClicked };
